import { getUser } from '../helpers/helperMethods';
import {
  PULSE_BATCH_ENABLED,
  PULSE_BATCH_ENABLED_ENV,
  PULSE_BATCH_MAX_EVENTS,
  PULSE_BATCH_MAX_WAIT_MS,
  PULSE_COLLECTOR_URL_ENV,
  PULSE_DEBUG_ENV,
  PULSE_KEEPALIVE_MAX_BYTES,
  PULSE_LOCK_RETRY_BACKOFF_MS,
  PULSE_RETRY_DELAYS_MS,
  PULSE_RETRY_ENABLED,
  PULSE_RETRY_ENABLED_ENV,
  PULSE_RETRY_MAX_ATTEMPTS,
  PULSE_TRACKER_ENABLED_ENV,
  PULSE_VISIBILITY_DEBOUNCE_MS,
} from './pulseConstants';
import {
  captureAttributionFromUrl,
  consumePendingAttribution,
  readCookieYesConsent,
  readCroVariants,
} from './pulseContext';
import { normalizePulseKeys } from './pulseKeyNormalization';
import { attachGa4IdsToEvents } from './ga4/ga4Ids';
import {
  createEmptyPulseEvent,
  type PulseEvent,
  type PulseEventName,
  type PulseEventPartial,
  type PulseSource,
} from './pulseSchema';
import {
  claimOutboundBatch,
  clearQueue,
  createPulseEventId,
  enqueueMany,
  enqueueOutbound,
  getOutboundBatch,
  getQueue,
  isStorageAvailable,
  type OutboundBatch,
  OUTBOUND_STORAGE_KEY,
  releaseOutboundLock,
  withOutboundLock,
} from './pulseStore';

type Identity = {
  user_id?: string | number | null;
  email?: string | null;
  phone_number?: string | null;
  new_user_indicator?: boolean | null;
};

export type PulseGa4MirrorHook = (event: PulseEvent) => void;

let pulseGa4MirrorHook: PulseGa4MirrorHook | null = null;

/** Registered by src/analytics/ga4 at init — mirrors when outbound/immediate Pulse sends fire (not fail-retry queue). */
export const registerPulseGa4MirrorHook = (hook: PulseGa4MirrorHook | null): void => {
  pulseGa4MirrorHook = hook;
};

const mirrorBuiltEventToGa4 = (event: PulseEvent): void => {
  if (!pulseGa4MirrorHook) return;
  try {
    pulseGa4MirrorHook(event);
  } catch {
    /* GA4 must never break Pulse send pipeline */
  }
};

let initialized = false;
let identity: Identity = {};
/** Fail-retry watcher timer. */
let retryTimer: ReturnType<typeof setTimeout> | null = null;
/**
 * The single pending outbound flush for this tab, plus when it is due.
 * Tracking the due time lets the scheduler refuse to push an earlier flush
 * later, which is also what keeps a burst of track() calls from fanning out
 * into one flush per event.
 */
let batchTimer: ReturnType<typeof setTimeout> | null = null;
let batchTimerDueAt: number | null = null;
/** Debounce for the tab-activation handoff. */
let visibilityTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityListenerBound = false;
/**
 * Completed retry attempts for the current fail-queue batch (0–3).
 * Also indexes PULSE_RETRY_DELAYS_MS for the *next* wait (30s, 1m, 2m).
 * Reset when the fail queue is cleared.
 */
let retryAttemptCount = 0;
let failFlushInFlight = false;

/** What triggered a flush — carried into debug logs only. */
type FlushReason = 'count' | 'timer' | 'visibility' | 'init' | 'storage' | 'manual';

const isPulseDebug = (): boolean =>
  String(process.env[PULSE_DEBUG_ENV] || '').toLowerCase() === 'true';

/**
 * Flush tracing, off unless REACT_APP_PULSE_DEBUG=true.
 * Every interesting outcome here is otherwise invisible: a claim skipped
 * because another tab holds the lock, gtag ids timing out, or the scheduler
 * declining to reschedule because something earlier is already pending.
 */
const pulseDebug = (event: string, detail: Record<string, unknown>): void => {
  if (!isPulseDebug()) return;
  try {
    // eslint-disable-next-line no-console
    console.info(`[pulse] ${event}`, detail);
  } catch (_) {}
};

const isEnabled = (): boolean =>
  String(process.env[PULSE_TRACKER_ENABLED_ENV] || '').toLowerCase() ===
  'true';

const isRetryEnabled = (): boolean => {
  if (!PULSE_RETRY_ENABLED) return false;
  const env = process.env[PULSE_RETRY_ENABLED_ENV];
  if (env === undefined || env === '') return true;
  return String(env).toLowerCase() === 'true';
};

const isBatchEnabled = (): boolean => {
  if (!PULSE_BATCH_ENABLED) return false;
  const env = process.env[PULSE_BATCH_ENABLED_ENV];
  if (env === undefined || env === '') return true;
  return String(env).toLowerCase() === 'true';
};

const collectorUrl = (): string | null => {
  const url = (process.env[PULSE_COLLECTOR_URL_ENV] || '').trim().replace(/\/$/, '');
  return url || null;
};

const isSuccessStatus = (status: number): boolean =>
  status === 200 || status === 202;

const safe = (fn: () => void): void => {
  try {
    fn();
  } catch (_) {
    // Tracking must never affect UX.
  }
};

const pageDefaults = () => {
  try {
    const path = window.location.pathname
      ? decodeURIComponent(window.location.pathname)
      : null;
    const href = window.location.href
      ? decodeURI(window.location.href)
      : null;
    return {
      page_url: href,
      page_path: path,
      page_id: null as string | null,
      // Only from campaign URL params (see attribution) — never document.referrer
      http_referrer: null as string | null,
    };
  } catch {
    return {
      page_url: null,
      page_path: null,
      page_id: null,
      http_referrer: null,
    };
  }
};

/** Explicit partial page fields win over window.location (e.g. checkout cart icon after redirect). */
const resolvePageFields = (
  partial: PulseEventPartial,
  fallback: ReturnType<typeof pageDefaults>
): { page_path: string | null; page_url: string | null } => {
  const page_path =
    partial.page_path !== undefined && partial.page_path !== null
      ? partial.page_path
      : fallback.page_path;

  if (partial.page_url !== undefined && partial.page_url !== null) {
    return { page_path, page_url: partial.page_url };
  }

  if (
    partial.page_path !== undefined &&
    partial.page_path !== null &&
    typeof window !== 'undefined'
  ) {
    try {
      return {
        page_path,
        page_url: decodeURI(`${window.location.origin}${partial.page_path}`),
      };
    } catch {
      return { page_path, page_url: fallback.page_url };
    }
  }

  return { page_path, page_url: fallback.page_url };
};

const resolveUserFields = () => {
  try {
    const user = getUser() || {};
    return {
      user_id: identity.user_id ?? user.id ?? null,
      email: identity.email ?? user.email ?? null,
      phone_number: identity.phone_number ?? user.phone ?? null,
      new_user_indicator:
        identity.new_user_indicator !== undefined
          ? identity.new_user_indicator
          : null,
    };
  } catch {
    return {
      user_id: identity.user_id ?? null,
      email: identity.email ?? null,
      phone_number: identity.phone_number ?? null,
      new_user_indicator: identity.new_user_indicator ?? null,
    };
  }
};

const resolvePulseSource = (): PulseSource => {
  try {
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches
    ) {
      return 'mobile_web';
    }
  } catch (_) {}
  return 'web';
};

export const buildEnvelope = (partial: PulseEventPartial): PulseEvent => {
  const base = createEmptyPulseEvent(partial.event_name);
  const consent = readCookieYesConsent();
  const userFields = resolveUserFields();
  const pageFallback = pageDefaults();
  const { page_path, page_url } = resolvePageFields(partial, pageFallback);
  const attribution = consumePendingAttribution();

  const event: PulseEvent = {
    ...base,
    ...partial,
    ...userFields,
    ...consent,
    page_path,
    page_url,
    schema_version: base.schema_version,
    source: resolvePulseSource(),
    currency: partial.currency || 'US',
    event_name: partial.event_name,
    event_id:
      partial.event_id ||
      createPulseEventId(
        partial.user_id !== undefined ? partial.user_id : userFields.user_id
      ),
    event_time: partial.event_time || Math.floor(Date.now() / 1000),
    cro_variants:
      partial.cro_variants !== undefined
        ? partial.cro_variants
        : readCroVariants(),
    snapchat_tracking_id: partial.snapchat_tracking_id ?? null,
    tiktok_tracking_id: partial.tiktok_tracking_id ?? null,
  };

  if (attribution) {
    event.click_id = attribution.click_id;
    event.utm_source = attribution.utm_source;
    event.utm_medium = attribution.utm_medium;
    event.utm_campaign = attribution.utm_campaign;
    event.page_id = attribution.page_id;
    event.http_referrer = attribution.http_referrer;
  } else {
    event.click_id = partial.click_id ?? null;
    event.utm_source = partial.utm_source ?? null;
    event.utm_medium = partial.utm_medium ?? null;
    event.utm_campaign = partial.utm_campaign ?? null;
    event.page_id = null;
    event.http_referrer = null;
  }

  return normalizePulseKeys({ ...base, ...event }) as PulseEvent;
};

/**
 * POST body is always an array: `[{...}]` or `[{...}, ...]`.
 *
 * GA4 mirroring runs first and synchronously, so it is never gated on the Pulse
 * response or on the gtag id lookup that follows.
 */
const sendEvents = async (
  events: PulseEvent[],
  { mirrorToGa4 = false }: { mirrorToGa4?: boolean } = {}
): Promise<boolean> => {
  if (!events.length) return true;
  const url = collectorUrl();
  if (!url) return false;

  if (mirrorToGa4) {
    for (const event of events) {
      mirrorBuiltEventToGa4(event);
    }
  }

  const payload = await attachGa4IdsToEvents(events);
  const body = JSON.stringify(payload);

  // Browsers reject keepalive requests with bodies over 64KB outright, which
  // would look like a network failure and send the whole batch to the retry
  // queue. Above the threshold, give up keepalive rather than the events.
  const useKeepalive = body.length <= PULSE_KEEPALIVE_MAX_BYTES;
  if (!useKeepalive) {
    pulseDebug('keepalive skipped (payload too large)', {
      bytes: body.length,
      events: events.length,
    });
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: useKeepalive,
      credentials: 'omit',
    });
    return isSuccessStatus(res.status);
  } catch {
    return false;
  }
};

// ——— Fail-retry queue watcher ———

const clearRetryTimer = (): void => {
  if (retryTimer != null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
};

const resetRetryCycle = (): void => {
  clearRetryTimer();
  retryAttemptCount = 0;
};

const nextRetryDelayMs = (): number => {
  const last = PULSE_RETRY_DELAYS_MS.length - 1;
  const idx = Math.min(retryAttemptCount, last);
  return PULSE_RETRY_DELAYS_MS[idx];
};

const ensureRetryWatcher = (): void => {
  if (!isRetryEnabled()) return;
  if (retryTimer != null) return;
  if (!getQueue().length) {
    resetRetryCycle();
    return;
  }
  if (retryAttemptCount >= PULSE_RETRY_MAX_ATTEMPTS) {
    return;
  }

  const delay = nextRetryDelayMs();
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void flushFailQueue();
  }, delay);
};

const moveToFailQueue = (events: PulseEvent[]): void => {
  if (!isRetryEnabled() || !events.length) return;
  const wasEmpty = getQueue().length === 0;
  enqueueMany(events);
  if (wasEmpty) retryAttemptCount = 0;
  ensureRetryWatcher();
};

/**
 * Send all fail-queued events in one API call.
 * Success → clear immediately.
 * Fail → retry up to PULSE_RETRY_MAX_ATTEMPTS, then clear regardless of outcome.
 */
export const flushFailQueue = async (): Promise<void> => {
  if (!isEnabled() || !isRetryEnabled()) return;
  if (failFlushInFlight) return;

  const batch = getQueue();
  if (!batch.length) {
    resetRetryCycle();
    return;
  }

  failFlushInFlight = true;
  try {
    const ok = await sendEvents(batch);
    retryAttemptCount += 1;

    if (ok || retryAttemptCount >= PULSE_RETRY_MAX_ATTEMPTS) {
      clearQueue();
      resetRetryCycle();
      return;
    }

    ensureRetryWatcher();
  } catch {
    retryAttemptCount += 1;

    if (retryAttemptCount >= PULSE_RETRY_MAX_ATTEMPTS) {
      clearQueue();
      resetRetryCycle();
      return;
    }

    ensureRetryWatcher();
  } finally {
    failFlushInFlight = false;
  }
};

/** @deprecated alias */
export const flushQueueBatch = flushFailQueue;
export const processQueue = flushFailQueue;

// ——— Outbound batch (PULSE_BATCH_MAX_EVENTS events / PULSE_BATCH_MAX_WAIT_MS) ———

const clearBatchTimer = (): void => {
  if (batchTimer != null) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }
  batchTimerDueAt = null;
};

/**
 * When this batch should go out, or null when there is nothing to send.
 *
 * A null openedAt means the stored age is unknown (legacy record), and a future
 * openedAt means the system clock moved. Both are treated as due now: the
 * alternative is a batch whose deadline never arrives.
 */
const dueAtFor = ({ events, openedAt }: OutboundBatch): number | null => {
  if (!events.length) return null;
  if (events.length >= PULSE_BATCH_MAX_EVENTS) return 0;
  if (openedAt == null) return 0;
  return Math.min(openedAt, Date.now()) + PULSE_BATCH_MAX_WAIT_MS;
};

/**
 * Schedule the next flush. Never pushes an already-earlier flush later, so this
 * doubles as the per-tab "a flush is already pending" guard.
 */
const scheduleBatchFlushAt = (dueAt: number, reason: FlushReason): void => {
  if (!isBatchEnabled()) return;
  if (batchTimer != null && batchTimerDueAt != null && batchTimerDueAt <= dueAt) {
    return;
  }

  clearBatchTimer();
  batchTimerDueAt = dueAt;
  batchTimer = setTimeout(() => {
    batchTimer = null;
    batchTimerDueAt = null;
    void flushOutboundBatch(reason);
  }, Math.max(0, dueAt - Date.now()));
};

/** Point the scheduler at whatever is currently queued. */
const scheduleForCurrentBatch = (reason: FlushReason): void => {
  const dueAt = dueAtFor(getOutboundBatch());
  if (dueAt == null) {
    clearBatchTimer();
    return;
  }
  scheduleBatchFlushAt(dueAt, reason);
};

/**
 * Flush the outbound localStorage batch in one API call.
 *
 * Claim-then-send: the events are removed from shared storage *before* the
 * network call, so events arriving mid-send join the next batch and a second tab
 * cannot claim the same snapshot. Claimed events are never written back to
 * outbound; a failed send routes them to the fail-retry queue instead.
 */
export const flushOutboundBatch = async (
  reason: FlushReason = 'manual'
): Promise<void> => {
  if (!isEnabled()) return;
  if (!getOutboundBatch().events.length) {
    clearBatchTimer();
    return;
  }

  let claimed: PulseEvent[] | null = null;
  try {
    claimed = await withOutboundLock(() =>
      claimOutboundBatch(PULSE_BATCH_MAX_EVENTS)
    );
  } catch {
    claimed = null;
  }

  // `null` means another tab holds the lock; `[]` means it raced us to an empty
  // queue. Never collapse these into a truthiness check: they need different
  // recovery, and the distinction is what the debug log reports.
  if (claimed === null) {
    // Back off rather than reschedule for "now": a batch at the count cap is
    // due immediately, so retrying at its natural due time would spin the main
    // thread for as long as the other tab holds the lock.
    pulseDebug('flush skipped', { reason, cause: 'locked-by-other-tab' });
    scheduleBatchFlushAt(Date.now() + PULSE_LOCK_RETRY_BACKOFF_MS, reason);
    return;
  }

  if (!claimed.length) {
    pulseDebug('flush skipped', { reason, cause: 'empty' });
    scheduleForCurrentBatch(reason);
    return;
  }

  // Re-arm before the await: a remainder already at the count cap goes out on
  // the next tick instead of waiting out the original 30s window.
  scheduleForCurrentBatch(reason);
  pulseDebug('flush', {
    reason,
    claimed: claimed.length,
    remaining: getOutboundBatch().events.length,
    nextDueInMs: batchTimerDueAt == null ? null : batchTimerDueAt - Date.now(),
  });

  try {
    const ok = await sendEvents(claimed, { mirrorToGa4: true });
    if (!ok) moveToFailQueue(claimed);
  } catch {
    moveToFailQueue(claimed);
  }
};

const queueOutbound = (event: PulseEvent): void => {
  enqueueOutbound(event);
  // dueAtFor returns 0 at the count cap, so this covers both triggers and,
  // critically, never clears the timer without replacing it.
  scheduleForCurrentBatch('count');
};

const sendEventImmediate = (event: PulseEvent): void => {
  void (async () => {
    try {
      const ok = await sendEvents([event], { mirrorToGa4: true });
      if (ok) return;
      moveToFailQueue([event]);
    } catch {
      moveToFailQueue([event]);
    }
  })();
};

/**
 * Fire-and-forget track. Never throws to callers.
 * With batching: localStorage outbound → flush on PULSE_BATCH_MAX_EVENTS or
 * PULSE_BATCH_MAX_WAIT_MS, whichever comes first.
 * Without batching: send immediately as `[{ event }]`.
 */
export const track = (partial: PulseEventPartial): void => {
  safe(() => {
    if (!isEnabled()) return;

    let event: PulseEvent;
    try {
      event = buildEnvelope(partial);
    } catch {
      return;
    }

    // Batching needs somewhere to batch. With storage blocked (private mode,
    // quota exceeded) writes fail silently, the count never grows and nothing
    // ever flushes, so every event would be lost — send directly instead.
    if (isBatchEnabled() && isStorageAvailable()) {
      queueOutbound(event);
      return;
    }

    sendEventImmediate(event);
  });
};

/**
 * Always POST immediately — skips outbound batch queue.
 * Failed sends still move to the fail-retry queue.
 */
export const trackImmediate = (partial: PulseEventPartial): void => {
  safe(() => {
    if (!isEnabled()) return;

    let event: PulseEvent;
    try {
      event = buildEnvelope(partial);
    } catch {
      return;
    }

    sendEventImmediate(event);
  });
};

/**
 * On boot / return: send anything left over from a previous session or tab.
 *
 * The outbound flush is scheduled rather than awaited so it lands after the
 * current tick. That keeps it independent of analytics init order: whoever
 * registers the GA4 mirror hook during the same tick is registered before the
 * flush runs, so a revisit batch is mirrored rather than silently skipped.
 */
const flushPersistedQueuesNow = (reason: FlushReason): void => {
  if (getOutboundBatch().events.length) {
    scheduleBatchFlushAt(Date.now(), reason);
  }
  if (isRetryEnabled() && getQueue().length) {
    retryAttemptCount = 0;
    void flushFailQueue();
  }
};

const onStorage = (ev: StorageEvent): void => {
  if (!isEnabled() || !isBatchEnabled()) return;
  if (ev.key !== OUTBOUND_STORAGE_KEY) return;
  scheduleForCurrentBatch('storage');
};

/**
 * A backgrounded tab cannot be trusted with the clock: browsers throttle its
 * timers to roughly a minute and may freeze the tab outright. So whenever a tab
 * becomes visible it takes over — flushing if the shared batch is already due,
 * otherwise re-arming the time cap from the shared openedAt.
 *
 * There is deliberately no hidden/pagehide send. An unload request cannot be
 * confirmed, so pairing it with a queue we keep would double-send every session
 * tail; leaving the events in localStorage delivers them exactly once on return.
 */
const onVisibilityChange = (): void => {
  if (!isEnabled() || !isBatchEnabled()) return;
  try {
    if (document.visibilityState !== 'visible') return;
  } catch {
    return;
  }

  if (visibilityTimer != null) clearTimeout(visibilityTimer);
  visibilityTimer = setTimeout(() => {
    visibilityTimer = null;
    safe(() => scheduleForCurrentBatch('visibility'));
  }, PULSE_VISIBILITY_DEBOUNCE_MS);
};

/** Test helper — reset module timers / flags. */
export const __resetRetryStateForTests = (): void => {
  clearRetryTimer();
  clearBatchTimer();
  if (visibilityTimer != null) {
    clearTimeout(visibilityTimer);
    visibilityTimer = null;
  }
  try {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  } catch (_) {}
  visibilityListenerBound = false;
  retryAttemptCount = 0;
  failFlushInFlight = false;
  initialized = false;
  pulseGa4MirrorHook = null;
  releaseOutboundLock();
};

export const init = (): void => {
  safe(() => {
    captureAttributionFromUrl();

    if (initialized) return;
    initialized = true;

    if (!isEnabled()) return;

    flushPersistedQueuesNow('init');

    try {
      window.addEventListener('online', () => {
        flushPersistedQueuesNow('init');
      });
      window.addEventListener('storage', onStorage);
      if (!visibilityListenerBound) {
        document.addEventListener('visibilitychange', onVisibilityChange);
        visibilityListenerBound = true;
      }
    } catch (_) {}
  });
};

export const setIdentity = (next: Identity): void => {
  safe(() => {
    identity = { ...identity, ...next };
  });
};

export const clearIdentity = (): void => {
  safe(() => {
    identity = {};
  });
};

/** Typed wrappers — call sites filled in later tickets. */
export const pulse = {
  init,
  track,
  setIdentity,
  clearIdentity,
  processQueue,
  flushQueueBatch,
  flushFailQueue,
  flushOutboundBatch,
  buildEnvelope,
  view: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'view' as PulseEventName }),
  click: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'click' as PulseEventName }),
  login: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'login' as PulseEventName }),
  signup: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'signup' as PulseEventName }),
  addToCart: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'add_to_cart' as PulseEventName }),
  removeFromCart: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'remove_from_cart' as PulseEventName }),
  viewCart: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'view_cart' as PulseEventName }),
  startCheckout: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    trackImmediate({ ...extra, event_name: 'start_checkout' as PulseEventName }),
  purchase: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'purchase' as PulseEventName }),
};

export default pulse;
